import { Box } from "@/components/ui/box";
import { RefreshControl } from "@/components/ui/refresh-control";
import { ScrollView } from "@/components/ui/scroll-view";
import { Spinner } from "@/components/ui/spinner";
import { VStack } from "@/components/ui/vstack";
import {
  useUserControllerMe,
  useUserPartControllerMyParticipants,
} from "@/generated/api/api";
import { EmptyState } from "../common/empty.state";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from "@/components/ui/select";
import { UserDTO, UserParticipantDTO } from "@/generated/api/models";
import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@/components/ui/icon";
import {
  FormControl,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import { useQRCodeDownloader } from "@/utils/useQRCode";
import QRCodeStyled from "react-native-qrcode-styled";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { getName } from "@/utils/name";
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import {
  Copy,
  Download,
  Lightbulb,
  LightbulbOff,
  Share,
  StoreIcon,
  TriangleAlert,
} from "lucide-react-native";
import { Platform } from "react-native";
import { Fab, FabIcon } from "@/components/ui/fab";
import * as Brightness from "expo-brightness";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { Link as ExpoLink } from "expo-router";
import { QRViewGuideAlert } from "../common/qr.view.guide.alert";

function QRBody(props: { participant: UserParticipantDTO; user: UserDTO }) {
  const { link, qrCodeRef, onCopy, onShare } = useQRCodeDownloader({
    participant: { ...props.participant, user: props.user },
  });

  return (
    <VStack className="gap-4">
      <Box className="flex-col md:flex-row items-center md:items-start w-full gap-4">
        <Box className="min-h-[200px] min-w-[200px]">
          <QRCodeStyled ref={qrCodeRef} data={link} padding={0} pieceSize={8} />
        </Box>
        <VStack className="gap-4 w-full flex-1">
          <VStack className="gap-1 w-full">
            <Heading>{getName(props.user)}</Heading>
            <Text size="sm">
              Your personal QR-code in{" "}
              <Text size="sm" bold>
                {props.participant.organization.name}
              </Text>
              . Share it with customers to let them leave tips to you.
            </Text>
            {props.participant.publicTags.length === 0 && (
              <Alert action="warning" variant="solid">
                <AlertIcon as={TriangleAlert} className="min-w-5" />
                <AlertText>
                  You may use this QR-code for printing, but it won't work until
                  at least one public tag is assigned to you. Please ask team
                  manager to assign it.
                </AlertText>
              </Alert>
            )}
          </VStack>
          <Box className="flex-row md:flex-col gap-2 md:w-fit justify-stretch">
            <Button onPress={onCopy} variant="outline" className="grow">
              <ButtonIcon as={Copy} size={20} className="text-typography-950" />
              <ButtonText>Copy link</ButtonText>
            </Button>
            <Button onPress={onShare} className="grow">
              <ButtonIcon as={Platform.OS === "web" ? Download : Share} />
              <ButtonText>
                {Platform.OS === "web" ? "Download PNG" : "Share"}
              </ButtonText>
            </Button>
          </Box>
        </VStack>
      </Box>
      <QRViewGuideAlert />
    </VStack>
  );
}

export function QRsPage() {
  const {
    data: participants,
    isLoading,
    isLoadingError,
    refetch,
    isRefetching,
  } = useUserPartControllerMyParticipants();

  const { data: me } = useUserControllerMe();

  const [selectedParticipant, setSelectedParticipant] =
    useState<UserParticipantDTO>();
  const [lightEnabled, setLightEnabled] = useState(false);
  const currentBrightness = useRef<number>();

  useEffect(() => {
    if (lightEnabled) {
      (async () => {
        const { status } = await Brightness.requestPermissionsAsync();
        if (status === "granted") {
          currentBrightness.current = await Brightness.getBrightnessAsync();
          Brightness.setBrightnessAsync(1);
        }
      })();
    } else if (currentBrightness.current !== undefined) {
      Brightness.setBrightnessAsync(currentBrightness.current);
    }
  }, [lightEnabled]);

  useEffect(() => {
    if (!selectedParticipant && !!participants?.length) {
      setSelectedParticipant(participants[0]);
    }
  }, [participants]);

  return (
    <>
      <Box className="w-full h-full">
        <ScrollView
          className="flex-1"
          contentContainerClassName="px-4 pt-4 pb-4 grow"
          refreshControl={
            <RefreshControl onRefresh={refetch} refreshing={isRefetching} />
          }
        >
          <VStack className="gap-4">
            {isLoading ? (
              <Spinner size="large" />
            ) : isLoadingError ? (
              <EmptyState
                title="Something went wrong"
                description="Please check your internet connection and try again"
              />
            ) : !participants?.length ? (
              <EmptyState title="No QR codes yet">
                <VStack className="gap-1 items-center">
                  <VStack className="gap-0 items-center">
                    <Text size="sm" className="text-center">
                      To start collecting tips, you need to be part of a
                      business team.
                    </Text>
                    <Text size="sm" className="text-center">
                      You can either join an existing business via an invite
                      link — or add your own in the Business tab.
                    </Text>
                  </VStack>
                  <ExpoLink href="/(tabs)/orgs" asChild>
                    <Button className="mt-2">
                      <ButtonIcon as={StoreIcon} />
                      <ButtonText>Go to Business</ButtonText>
                    </Button>
                  </ExpoLink>
                </VStack>
              </EmptyState>
            ) : (
              <>
                <FormControl>
                  <FormControlLabel>
                    <FormControlLabelText>Select business</FormControlLabelText>
                  </FormControlLabel>
                  <Select
                    onValueChange={(arg) =>
                      setSelectedParticipant(
                        participants.find((p) => p.id === arg)
                      )
                    }
                    selectedValue={selectedParticipant?.id}
                  >
                    <SelectTrigger>
                      <SelectInput
                        placeholder="Select business"
                        className="flex-1"
                        value={selectedParticipant?.organization.name}
                      />
                      <SelectIcon className="mr-3" as={ChevronDownIcon} />
                    </SelectTrigger>
                    <SelectPortal snapPoints={[20]}>
                      <SelectBackdrop />
                      <SelectContent>
                        <SelectDragIndicatorWrapper>
                          <SelectDragIndicator />
                        </SelectDragIndicatorWrapper>
                        <SelectScrollView>
                          {participants.map((p) => (
                            <SelectItem
                              key={p.id}
                              label={p.organization.name}
                              value={p.id}
                            />
                          ))}
                        </SelectScrollView>
                      </SelectContent>
                    </SelectPortal>
                  </Select>
                </FormControl>
                {selectedParticipant && me && (
                  <QRBody participant={selectedParticipant} user={me} />
                )}
              </>
            )}
          </VStack>
        </ScrollView>
      </Box>
      {Platform.OS !== "web" && (
        <Fab
          size="lg"
          placement="bottom right"
          isHovered={false}
          isDisabled={false}
          isPressed={false}
          isFocused={true}
          onPress={() => setLightEnabled((val) => !val)}
        >
          <FabIcon as={lightEnabled ? Lightbulb : LightbulbOff} />
        </Fab>
      )}
    </>
  );
}
